import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { AdminCourseModeStatus } from '@edtech-ui/constants/admin-course-mode';
import { CreateQuizPayload } from '@edtech-ui/core/models/quiz-creation/create-quiz';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-add-quiz-assesment-dialog',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule,FormsModule],
	templateUrl: './add-quiz-assesment-dialog.component.html',
	styleUrl: './add-quiz-assesment-dialog.component.scss'
})
export class AddQuizAssesmentDialogComponent {
	isLoading = false;
	isModalOpen = false;
	isCreating = false;
	isDropdownOpen = false;
	openMenuId: number | null = null;
	page = 1;
	limit = 10;
	totalRecords = 0;
	totalPages = 0;
	visiblePages: number[] = [];
	totalQuiz: number = 0;
	loading = false;
	classroomId = environment.classroomId;
	searchText = '';
	courses: { id: string; name: string }[] = [];
	selectedCourse: { id: string; name: string } | null = null;
	subscription = new Subscription();

	quizForm = new FormGroup({
		quiz_name: new FormControl<string>('', {
			nonNullable: true,
			validators: Validators.required
		}),
		hours: new FormControl<number>(0),
		minutes: new FormControl<number>(0),
		seconds: new FormControl<number>(0),
	});

	constructor(
		private router: Router,
		private quizService: QuizManagementService,
		private toastr: ToastrService,
		private courseService: CourseManagementService,
		private dialogRef: MatDialogRef<AddQuizAssesmentDialogComponent>,
	) { }

	ngOnInit(): void {
		this.loadCourses();
	}



	closeDialog() {
		this.dialogRef.close()
	}


	get filteredCourses(): { id: string; name: string }[] {
		const search = this.searchText.trim().toLowerCase();

		if (!search) return this.courses;

		return this.courses.filter(course =>
			course.name.toLowerCase().includes(search)
		);
	}




	openDropdown(): void {
		this.isDropdownOpen = true;
	}

	selectCourse(course: { id: string; name: string }): void {
		this.selectedCourse = course;
		this.searchText = course.name;
		this.isDropdownOpen = false;
	}

	loadCourses(): void {
		if (this.loading) return;
		this.loading = true;

		this.courseService.getCourses(this.classroomId, this.page, this.limit, 'created_at', 'desc', 'all').subscribe({
			next: res => {
				this.loading = false;

				if (res?.Success && res.Data?.data) {
					const publishedCourses = res.Data.data.filter((course: any) => course.status === 'Published')
						.map((course: any) => ({
							id: course.course_id,
							name: course.course_title,
							status: course.status
						}));
					this.courses = [...this.courses, ...publishedCourses];

					this.page = res.Data.page + 1;
					this.totalPages = res.Data.total_pages;
				}
			},
			error: () => {
				this.loading = false;
			}
		});
	}


	hasMore(): boolean {
		return this.page <= this.totalPages;
	}

	createQuiz(): void {
		if (!this.selectedCourse) {
			this.toastr.error('Please select a course');
			return;
		}

		const quizName = this.quizForm.controls.quiz_name.value.trim();
		if (!quizName) {
			this.toastr.error('Quiz name is required');
			return;
		}

		const hours = this.quizForm.controls.hours.value ?? 0;
		const minutes = this.quizForm.controls.minutes.value ?? 0;
		const seconds = this.quizForm.controls.seconds.value ?? 0;

		const timeLimit = hours * 3600 + minutes * 60 + seconds;

		if (timeLimit === 0) {
			this.toastr.error('Please set a time limit');
			return;
		}

		const payload: CreateQuizPayload = {
			quiz_name: quizName,
			time_limit: timeLimit,
			course_id: this.selectedCourse.id,
			mode:AdminCourseModeStatus.COURSE_QUIZ
		};

		this.isCreating = true;

		this.subscription.add(
			this.quizService
				.postCreateQuiz(payload, this.classroomId)
				.pipe(
					tap(res => {
						if (res?.Success) {
							this.closeDialog();
							this.router.navigate(['/admin-student-platform/create-quiz',this.classroomId,res.Data.quiz_id]);
						} else {
							this.toastr.error(res?.Message);
						}
					}),
					catchError(() => {
						this.toastr.error('Something went wrong while creating the quiz.');
						return of(null);
					}),
					finalize(() => (this.isCreating = false))
				).subscribe()
		);
	}



	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}